import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";

import Button from "veneer/Button";
import { type LngLat, Popup as VeneerPopup } from "veneer/Map";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Tooltip from "veneer/Tooltip";
import { OverflowTooltipTypography } from "veneer/Typography";

import Indicator, {
  type IndicatorColor,
  type IndicatorType,
} from "cortex/ui/src/ui/indicator";

export interface DevicePopupState {
  coords: LngLat;
  indicatorColor?: IndicatorColor;
  id: string;
  markerLabel: string;
  markerIndicator?: IndicatorType;
  name: string;
  networkName: string;
}

interface DevicePopupProps {
  onClose: () => void;
  onViewDetails?: (deviceId: string) => void;
  popupState: DevicePopupState;
}

function getStatusBadgeBackgroundColor(indicatorColor?: IndicatorColor) {
  if (indicatorColor === "green") {
    return {
      backgroundColor: Colors.green200,
    };
  }

  if (indicatorColor === "gray") {
    return {
      backgroundColor: Colors.gray100,
    };
  }

  if (indicatorColor === "red") {
    return {
      backgroundColor: Colors.red100,
    };
  }

  return {
    backgroundColor: Colors.yellow100,
  };
}

export function DevicePopup({
  onClose,
  onViewDetails,
  popupState,
}: DevicePopupProps) {
  const history = useHistory();
  const [isCopied, setIsCopied] = useState(false);
  const statusBadgeColors = getStatusBadgeBackgroundColor(
    popupState.indicatorColor,
  );

  useEffect(() => {
    if (!isCopied) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsCopied(false);
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isCopied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(popupState.id);
      setIsCopied(true);
    } catch {
      setIsCopied(false);
    }
  };

  const handleViewDetails = () => {
    if (!onViewDetails) {
      return;
    }
    onViewDetails(popupState.id);
  };

  const handleCardClick = () => {
    history.push(`/devices/${popupState.id}/overview`);
    onClose();
  };

  return (
    <VeneerPopup
      closeButton={false}
      latitude={popupState.coords[1]}
      longitude={popupState.coords[0]}
      maxWidth="360px"
      onClose={onClose}
    >
      <PopupCard data-testid="map-device-popup" onClick={handleCardClick}>
        <Stack direction="row" spacing={1.5}>
          <StatusBadge
            style={{
              backgroundColor: statusBadgeColors.backgroundColor,
            }}
          >
            <Tooltip title={popupState.markerLabel}>
              <Indicator
                color={popupState.indicatorColor ?? "gray"}
                size="small"
                type={popupState.markerIndicator ?? "circle"}
              />
            </Tooltip>
          </StatusBadge>
          <Stack flex={1} pr={2} spacing={0.25}>
            <OverflowTooltipTypography variant="body2">
              {popupState.networkName}
            </OverflowTooltipTypography>
            <OverflowTooltipTypography color={Colors.gray1000} variant="h5">
              {popupState.name}
            </OverflowTooltipTypography>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" spacing={1.5}>
          <Button
            color={isCopied ? "success" : undefined}
            icon="copyFilled"
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
            variant="text"
          >
            {isCopied ? "Copied" : "Device ID"}
          </Button>
          {onViewDetails ? (
            <Button
              data-testid="map-device-popup-details-button"
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails();
              }}
              variant="text"
            >
              View Details
            </Button>
          ) : (
            <Link to={`/devices/${popupState.id}/overview`}>
              <Button
                data-testid="map-device-popup-details-button"
                variant="text"
              >
                View Details
              </Button>
            </Link>
          )}
        </Stack>
      </PopupCard>
    </VeneerPopup>
  );
}

const PopupCard = styled(Stack)(({ theme }) => ({
  flexDirection: "column",
  gap: theme.spacing(1),
  maxWidth: 360,
  minWidth: 320,
  padding: theme.spacing(1.5),
  "&:hover": { backgroundColor: Colors.lightestGraySemiOpaque },
  cursor: "pointer",
}));

const StatusBadge = styled("div")({
  alignItems: "center",
  borderRadius: "50%",
  display: "flex",
  flexShrink: 0,
  height: 40,
  justifyContent: "center",
  width: 40,
});
